// Tipos para Questionários

import type { RiskLevel } from './patient.types';

export type SendStatus = 'PENDING' | 'SENT' | 'DELIVERED' | 'ANSWERED' | 'EXPIRED' | 'FAILED';

export type DeliveryChannel = 'WHATSAPP' | 'EMAIL' | 'SMS' | 'PORTAL';

export interface QuestionnaireDefinition {
  id: string;
  code: string;
  name: string;
  description?: string;
  estimatedMinutes?: number;
  questionCount: number;
  active: boolean;
}

export interface QuestionnaireSend {
  id: string;
  patientId: string;
  patientName: string;
  questionnaireId: string;
  questionnaireCode: string;
  questionnaireName: string;
  careLineId?: string;
  careLineName?: string;
  scheduledAt?: string;
  sentAt?: string;
  sentVia?: DeliveryChannel;
  status: SendStatus;
  expiresAt?: string;
  reminderCount: number;
  answeredAt?: string;
  responseId?: string;
  createdByName?: string;
  createdAt: string;
}

export interface QuestionnaireResponse {
  id: string;
  sendId: string;
  patientId: string;
  patientName: string;
  questionnaireId: string;
  questionnaireCode: string;
  questionnaireName: string;
  answers: Record<string, unknown>;
  scores: Record<string, unknown>;
  totalScore?: number;
  riskLevel?: RiskLevel;
  alertTriggered: boolean;
  alertMessage?: string;
  answeredAt: string;
  durationSeconds?: number;
  reviewedByName?: string;
  reviewedAt?: string;
  reviewNotes?: string;
}

export interface QuestionnaireStats {
  totalPending: number;
  totalAnswered: number;
  totalExpired: number;
  alertsPendingReview: number;
  byQuestionnaire: {
    code: string;
    name: string;
    pending: number;
    answered: number;
  }[];
}

// Labels para exibição
export const SendStatusLabels: Record<SendStatus, string> = {
  PENDING: 'Pendente',
  SENT: 'Enviado',
  DELIVERED: 'Entregue',
  ANSWERED: 'Respondido',
  EXPIRED: 'Expirado',
  FAILED: 'Falha',
};

export const SendStatusColors: Record<SendStatus, { bg: string; text: string }> = {
  PENDING: { bg: 'bg-gray-100', text: 'text-gray-800' },
  SENT: { bg: 'bg-blue-100', text: 'text-blue-800' },
  DELIVERED: { bg: 'bg-cyan-100', text: 'text-cyan-800' },
  ANSWERED: { bg: 'bg-green-100', text: 'text-green-800' },
  EXPIRED: { bg: 'bg-orange-100', text: 'text-orange-800' },
  FAILED: { bg: 'bg-red-100', text: 'text-red-800' },
};

export const DeliveryChannelLabels: Record<DeliveryChannel, string> = {
  WHATSAPP: 'WhatsApp',
  EMAIL: 'E-mail',
  SMS: 'SMS',
  PORTAL: 'Portal',
};

// Types for questionnaire definition with questions
export type QuestionType = 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE' | 'SCALE' | 'TEXT' | 'NUMBER' | 'DATE';

export interface QuestionOption {
  value: number;
  label: string;
  description?: string;
}

export interface Question {
  order: number;
  code: string;
  text: string;
  type: QuestionType;
  options?: QuestionOption[];
  required: boolean;
  alertCondition?: string;
  helpText?: string;
}

export interface QuestionnaireDefinitionFull {
  id: string;
  code: string;
  name: string;
  description?: string;
  instructions?: string;
  questions: Question[];
  estimatedMinutes?: number;
  version: number;
  active: boolean;
}

// For portal - questionnaire to answer
export interface QuestionnaireForPatient {
  accessToken: string;
  patientId?: string;
  patientName: string;
  questionnaireCode?: string;
  questionnaireName: string;
  instructions?: string;
  questions: Question[];
  estimatedMinutes?: number;
  expiresAt?: string;
}

// Request types
export interface SendQuestionnaireRequest {
  patientId: string;
  questionnaireCode: string;
  careLineId?: string;
  channel?: DeliveryChannel;
  scheduledAt?: string;
  expirationDays?: number;
}

export interface AnswerQuestionnaireRequest {
  accessToken: string;
  answers: Record<string, unknown>;
  durationSeconds?: number;
}

export interface ReviewResponseRequest {
  notes: string;
}
